import { g, layers, LayerClass } from './globals';
import { EventBus, VectorSelection } from '@hcie/shared';
// We use events to notify the UI instead of direct imports to break circular dependencies.
import { DocumentState } from './types';

declare global {
    interface Window {
        applyZoom(zoom: number): void;
        openNewImageDialog(): void;
        vectorSelection: VectorSelection | null;
        dispatchEvent(event: Event): boolean;
    }
}

export class ImageDocument implements DocumentState {
    id: string;
    name: string;
    width: number;
    height: number;
    layers: LayerClass[];
    activeLayerIndex: number;
    undoStack: any[];
    undoIndex: number;
    zoom: number;
    scrollLeft: number;
    scrollTop: number;
    modified: boolean;
    filepath: string;
    selectionActive: boolean = false;
    selectionMask: any = null;
    selectionBorder: any[] = [];
    selectionCanvas: HTMLCanvasElement | null = null;
    vectorSelection: VectorSelection | null = null;

    constructor(name = "Untitled", width = 500, height = 500) {
        this.id = Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        this.name = name;
        this.filepath = '';
        this.width = width;
        this.height = height;
        this.layers = [new LayerClass("Background", width, height)];
        this.activeLayerIndex = 0;
        this.undoStack = [];
        this.undoIndex = -1;
        this.zoom = 1.0;
        this.scrollLeft = 0;
        this.scrollTop = 0;
        this.modified = false;
    }
}

// Document management
if (!g.documents) (g as any).documents = [];
if (g.activeDocumentIndex === undefined) (g as any).activeDocumentIndex = -1;

/**
 * Create the initial document from the existing state
 */
export function initDocuments() {
    if (g.documents.length === 0) {
        const doc = new ImageDocument("Image 1", g.image_width, g.image_height);
        // Transfer existing layers into the first document
        doc.layers = [...layers];
        doc.activeLayerIndex = g.activeLayerIndex;
        doc.zoom = g.zoom;
        doc.filepath = g.filepath;
        g.documents.push(doc);
        (g as any).activeDocumentIndex = 0;
    }
    switchDocument(0);
}

/**
 * Create a new blank document
 */
export function newDocument(name?: string, width?: number, height?: number) {
    const docName = name || `Image ${g.documents.length + 1}`;
    const w = width || g.image_width;
    const h = height || g.image_height;
    const doc = new ImageDocument(docName, w, h);
    g.documents.push(doc);
    switchDocument(g.documents.length - 1);
}

/**
 * Close a document by index
 */
/**
 * Close a document by index
 */
export function closeDocument(index: number) {
    if (index < 0 || index >= g.documents.length) return;
    
    console.log(`[Document] Closing document at index ${index}. Current total: ${g.documents.length}`);

    // If we are closing the active document or everything, save state first (though we are deleting it)
    // Actually, we should just ensure we aren't leaking state.
    
    g.documents.splice(index, 1);
    
    if (g.documents.length === 0) {
        return closeAllDocuments();
    }

    // Adjust active index if it was at or after the closed index
    let nextIndex = g.activeDocumentIndex;
    if (index <= g.activeDocumentIndex) {
        nextIndex = Math.max(0, g.activeDocumentIndex - 1);
    }
    
    if (nextIndex >= g.documents.length) {
        nextIndex = g.documents.length - 1;
    }

    console.log(`[Document] Switching to next available document at index ${nextIndex}`);
    switchDocument(nextIndex);
}

/**
 * Close all active documents
 */
export function closeAllDocuments() {
    g.documents.length = 0;
    (g as any).activeDocumentIndex = -1;
    layers.length = 0;
    g.image_width = 800; // Reset to safe defaults
    g.image_height = 600;
    
    // AI_FIX: Physically reset and clear the canvas elements to prevent "ghosting"
    if (typeof window !== 'undefined') {
        if ((window as any).resizeCanvas) {
            (window as any).resizeCanvas(g.image_width, g.image_height);
        } else {
            const can = document.getElementById('drawingCanvas') as HTMLCanvasElement;
            if (can) {
                can.width = g.image_width;
                can.height = g.image_height;
                const ctx = can.getContext('2d');
                if (ctx) ctx.clearRect(0, 0, can.width, can.height);
            }
        }
    }

    // Notify UI that no docs left
    notifyTabsChanged();
}

// Event Listeners for External Control (#2069)
EventBus.on('CLOSE_DOCUMENT_CORE', (data: { index: number }) => {
    closeDocument(data.index);
});

/**
 * Save current state to the active document before switching
 */
export function saveCurrentDocumentState() {
    const doc = g.documents[g.activeDocumentIndex] as ImageDocument;
    if (!doc) return;

    doc.layers = [...layers];
    doc.activeLayerIndex = g.activeLayerIndex;
    doc.zoom = g.zoom;
    doc.filepath = g.filepath;

    const container = document.getElementById('canvasScrollArea');
    if (container) {
        doc.scrollLeft = container.scrollLeft;
        doc.scrollTop = container.scrollTop;
    }

    // AI_GUARDRAIL: Document State Isolation
    // Selection state (active, mask, border) MUST be saved per document.
    doc.selectionActive = g.isSelectionActive;
    doc.selectionMask = g.selectionMask;
    doc.selectionBorder = g.selectionBorder;
    doc.selectionCanvas = g.selectionCanvas as HTMLCanvasElement;
    doc.vectorSelection = (window as any).vectorSelection || null;
}

/**
 * Switch to a different document
 */
export function switchDocument(index: number) {
    if (index < 0 || index >= g.documents.length) return;
    if (g.activeDocumentIndex >= 0 && g.activeDocumentIndex < g.documents.length) {
        saveCurrentDocumentState();
    }

    (g as any).activeDocumentIndex = index;
    const doc = g.documents[index] as ImageDocument;

    // AI_FIX: Full state isolation to prevent ghosting from any tool
    g.drawing = false;
    (g as any).floatingContent = null;
    
    // Reset vector tool manager state
    const vtm = (window as any).vectorToolManager;
    if (vtm && typeof vtm.cancelEdit === 'function') {
        vtm.cancelEdit();
    }

    const tempCan = (window as any).tempCanvas || document.getElementById('tempCanvas') as HTMLCanvasElement;
    if (tempCan) {
        const tCtx = tempCan.getContext('2d');
        if (tCtx) tCtx.clearRect(0, 0, tempCan.width, tempCan.height);
    }

    if (typeof window !== 'undefined') {
        const can = document.getElementById('drawingCanvas') as HTMLCanvasElement;
        if (can && (window as any).resizeCanvas) {
            (window as any).resizeCanvas(doc.width, doc.height);
        } else if (can) {
            can.width = doc.width;
            can.height = doc.height;
        }
    }

    // Restore document state to globals
    layers.splice(0, layers.length, ...doc.layers);
    
    g.activeLayerIndex = doc.activeLayerIndex;
    g.zoom = doc.zoom;
    g.image_width = doc.width;
    g.image_height = doc.height;
    g.filepath = doc.filepath || '';

    // Restore Selection State
    g.isSelectionActive = doc.selectionActive || false;
    g.selectionMask = doc.selectionMask || null;
    g.selectionBorder = doc.selectionBorder || [];
    g.selectionCanvas = doc.selectionCanvas || null;
    (window as any).vectorSelection = doc.vectorSelection || null;

    // Trigger properties panel update if needed
    EventBus.emit('VECTOR_SHAPE_SELECTION_CHANGED', null);

    // Re-render layers and UI via Event Bus
    EventBus.emit('DOCUMENT_SWITCHED', { index });

    notifyTabsChanged();
    console.log(`Switched to document "${doc.name}"`);
}

/**
 * Notifies the UI that document tabs need re-rendering.
 */
export function notifyTabsChanged() {
    const tabData = g.documents.map((doc: any, i: number) => ({
        name: doc.name,
        index: i,
        active: i === g.activeDocumentIndex,
        modified: doc.modified
    }));
    EventBus.emit('DOCUMENT_TABS_CHANGED', { tabs: tabData });
}

// No more window bindings in core document.ts.
